import { computed, unref, toValue, type MaybeRefOrGetter } from 'vue';
import { compareColumnDisplayOrder, isColumnShown } from '@/utils/columnConfigSnapshot';
import {
  usePersistentTableStore,
  type SearchViewMode,
  type GeoRadiusPin,
  type MapCardFieldKey,
  type TableLayoutMode,
} from '@/stores/persistentTableStore';
import type { JsonModel } from "@/types/columnTypes";
import { groupFirstSort } from '@/components/DataTable/groupBoundaries';
import { useEntityStore } from '@/stores/entityStore';
import { useQuickSearchSelectionStore } from '@/stores/quickSearchSelectionStore';
import { withQuickSearchSelection } from '@/utils/listSearch';
import JsonModels from '@/ts/models.json';
import type { IScope } from '@/types/filterBuilderTypes';
import { buildEntityColumns } from '@/composables/entityColumns';
import type { TableColumn, TableColumnStateProps, TableColumnStaticProps } from '@/types/tableTypes';
import { useRoute } from 'vue-router';
import {
  customListScheduleFieldOf,
  extraFilterColumns,
  filterControlMappings,
} from '@/components/FilterBuilder/filterControlRegistry';
import { cardDefaultColumnIdsFor } from '@/composables/searchInspectorRegistry';
import { resolvePageLevelEntityId } from '@/composables/pageLevelTableEntities';
// PAGE_LEVEL_SCHEMAS moved to its own module so saved-view expansion can read the
// same map without pulling in a composable that calls useRoute().
import { PAGE_LEVEL_SCHEMAS } from '@/composables/entityColumnUniverse';


/**
 * The custom list's own filter columns, headed "Custom List" so the picker lists them
 * apart from the "Schedule" group. The old "schedules" column matched text in
 * `location_zone_custom_playlist_schedules.display_string`, and the Schedule group
 * replaces it. So a new rule cannot pick it. A saved rule that uses it still runs, and
 * its row still shows the column, so the column stays in this list, marked hidden.
 */
function customListFilterColumns(modelColumns: TableColumn[]): TableColumn[] {
  return modelColumns.map((column) => ({
    ...column,
    filterGroup: 'Custom List',
    ...(column.id === 'location_zone_custom_playlist_schedules' ? { hiddenFromFilterPicker: true } : {}),
  }));
}

/**
 * `additionalColumns` accepts a plain array, a ref, or a getter so callers whose extra
 * columns depend on the selected entity (e.g. the list page's per-entity Play column)
 * can pass a computed and have `baseColumns` recompute when the entity changes.
 */
export function usePersistentTable(options?: {
  additionalColumns?: MaybeRefOrGetter<TableColumnStaticProps[]>;
}) {
  const route = useRoute();
  const store = usePersistentTableStore();
  const entityStore = useEntityStore();
  const quickSearchSelectionStore = useQuickSearchSelectionStore();

  const selectedEntityId = computed(() => {
    // Persistent-table state is scoped per entity id. Pages that don't drive
    // the entity selector (track-matching, audio-file-issues) opt out via the
    // route name so their filter / column / page state stays isolated.
    if (route.name === 'track-matching') {
      return 'LiveTrack';
    }
    const routeName = typeof route.name === 'string' ? route.name : '';
    return resolvePageLevelEntityId(routeName) ?? entityStore.selectedEntityId;
  });

  /** Page-level (non-model.json) schema scope — see {@link PAGE_LEVEL_SCHEMAS}. */
  const pageLevelColumns = computed<TableColumnStaticProps[] | undefined>(
    () => PAGE_LEVEL_SCHEMAS[selectedEntityId.value],
  );
  const isPageLevelSchemaScope = computed(() => pageLevelColumns.value !== undefined);

  const model = computed(() => {
    return JsonModels[
        selectedEntityId.value.replace(/\s+/g, '') as keyof typeof JsonModels
      ] as JsonModel;
  });

  const baseColumns = computed(() => {
    // Page-level report scopes (audio-file-issues, playlist-switching) have no
    // Eloquent-model-derived schema — their columns span multiple tables or are
    // SQL aggregates — so use the hand-rolled column set that mirrors the
    // matching backend FilterSchema class.
    if (pageLevelColumns.value) {
      const scopeFilterControls = filterControlMappings[selectedEntityId.value] || {};
      let columns = pageLevelColumns.value.map((column) => {
        const filterControl = scopeFilterControls[column.id];
        return filterControl ? { ...column, filterControl } : column;
      });
      const extraColumns = toValue(options?.additionalColumns);
      if (extraColumns && extraColumns.length > 0) {
        columns = extraColumns.concat(columns);
      }
      return columns;
    }

    let columns = buildEntityColumns(selectedEntityId.value);

    const extraColumns = toValue(options?.additionalColumns);
    if (extraColumns && extraColumns.length > 0) {
      columns = extraColumns.concat(columns);
    }

    return columns;
  });

  const columns = computed(() => {
    return baseColumns.value.map((col, index): TableColumn => {
        const stateColumns = {
          isVisible: isColumnShown(col, store.columnHidden),
          isPinned: store.columnPin[col.id] ?? false,
          isSorted: Boolean(store.columnSort[col.field]),
          order: store.columnOrder[col.id] ?? index + 1,
          sortDirection: store.columnSort[col.field] ?? undefined,
          // fixedWidth columns ignore any persisted/auto size — they are always one width.
          size: col.fixedWidth ?? store.columnSize[col.id] ?? 150,
        } as TableColumnStateProps;

        return {
          ...col,
          ...stateColumns,
        };
      }
    ).sort((left, right) => compareColumnDisplayOrder(left.order, right.order));
  });

  const absoluteOrderedColumns = computed(() => [...columns.value].sort((a, b) => Math.abs(a.order) - Math.abs(b.order)));

  const pinnedColumns = computed(() => {
    return columns.value.filter(col => col.isPinned);
  });

  const unpinnedColumns = computed(() => {
    return columns.value.filter(col => !col.isPinned)
  });

  const visibleNonPinnedColumns = computed(() =>
    unpinnedColumns.value.filter(col => col.isVisible)
  );

  const visiblePinnedColumns = computed(() =>
    pinnedColumns.value.filter(col => col.isVisible)
  );

  /** Columns to show in the filter builder: only _searchable fields when present, otherwise all columns */
  const columnsForFilter = computed(() => {
    const cols = columns.value;
    // Page-level report scopes have no model.json backing: filterability is
    // declared per-column in the hand-rolled schema, so skip the _searchable
    // narrowing and honour each column's isFilterable flag.
    if (isPageLevelSchemaScope.value) {
      return cols.filter(col => col.isFilterable !== false);
    }
    // Filter-only columns (e.g. LiveTrack "Used By Account") are appended after the
    // _searchable narrowing — they exist only in the filter builder, not the table.
    // The selected entity can arrive as a display name ("Live Track"); normalize to
    // the model key the registry uses, same as the JsonModels lookup above.
    const filterOnlyColumns = (extraFilterColumns[selectedEntityId.value.replace(/\s+/g, '')] ?? []) as TableColumn[];

    const currentModel = unref(model) as { _searchable?: string[] } | undefined;
    const searchable = currentModel?._searchable;
    // A column that says it cannot be filtered, such as a login password, is never offered.
    const filterableColumns = cols.filter(col => col.isFilterable !== false);
    const modelColumns = !searchable || !Array.isArray(searchable) || searchable.length === 0
      ? filterableColumns
      : filterableColumns.filter(col => new Set(searchable).has(col.id));
    if (selectedEntityId.value === 'LocationZoneCustomPlaylist') {
      return customListFilterColumns(modelColumns).concat(filterOnlyColumns);
    }
    return modelColumns.concat(filterOnlyColumns);
  });

  const columnSort = computed(() => store.columnSort);

  /** The column the list is grouped by, or null. See DataTable/groupBoundaries.ts. */
  const groupBy = computed(() => store.groupBy);
  const setGroupBy = (field: string | null) => {
    store.groupBy = field;
  };
  /** The sort the server gets: while grouping, the group column comes first. */
  const effectiveSort = computed(() => groupFirstSort(store.columnSort, store.groupBy));

  const toggleSort = (columnField: string) => {
    const currentSort = store.columnSort[columnField];

    if (!currentSort) {
      store.columnSort[columnField] = 'asc';
    } else if (currentSort === 'asc') {
      store.columnSort[columnField] = 'desc';
    } else {
      delete store.columnSort[columnField];
    }
  };

  const reorderColumns = (newOrder: Record<string, number>) => {
    store.columnOrder = newOrder;
  };

  const setColumnSize = (columnId: string, size: number) => {
    store.columnSize[columnId] = size;
  };

  const updateColumnState = (newColumns: TableColumn[]) => {
    if (!newColumns || newColumns.length === 0) return;
    
    const newColumnHidden: Record<string, boolean> = {};
    const newColumnPin: Record<string, boolean> = {};
    const newColumnOrder: Record<string, number> = {};
    const newColumnSize: Record<string, number> = {};

    newColumns.forEach((col, index) => {
      if (col && col.id) {
        newColumnHidden[col.id] = !col.isVisible;
        newColumnPin[col.id] = col.isPinned;
        newColumnOrder[col.id] = col.order ?? index;
        newColumnSize[col.id] = col.size;
      }
    });

    store.columnHidden = newColumnHidden;
    store.columnPin = newColumnPin;
    store.columnOrder = newColumnOrder;
    store.columnSize = newColumnSize;
  };

  const pageIndex = computed(() => store.pageIndex);
  const pageSize = computed(() => store.pageSize);

  const setPageIndex = (index: number) => {
    store.pageIndex = index;
  };

  const setPageSize = (size: number) => {
    store.pageSize = size;
    store.pageIndex = 0;
  };

  const filter = computed<IScope>(() => store.filter.ruleScopes);
  /**
   * The filter every request for this list sends: the list, the count, export and bulk
   * actions. The list search rule gets the person's quick search fields and mode here,
   * and only here, so the filter kept for saved views holds just the text (SAU-467).
   */
  const compositeFilter = computed(() => ({
    ...store.filter,
    ruleScopes: withQuickSearchSelection(store.filter.ruleScopes, quickSearchSelectionStore.selectionFor(selectedEntityId.value)),
  }));

  const searchViewMode = computed<SearchViewMode>(() => store.searchViewMode);

  const setSearchViewMode = (mode: SearchViewMode) => {
    store.searchViewMode = mode;
  };

  const tableLayout = computed<TableLayoutMode>(() => store.tableLayout);

  const setTableLayout = (mode: TableLayoutMode) => {
    store.tableLayout = mode;
  };

  const mapCardFieldVisibility = computed<Record<MapCardFieldKey, boolean>>(() => store.mapCardFieldVisibility);

  const setMapCardFieldVisibility = (next: Record<MapCardFieldKey, boolean>) => {
    store.setMapCardFieldVisibility(next);
  };

  const setFilter = (newFilter: IScope) => {
    store.filter = { ruleScopes: newFilter };
    store.pageIndex = 0;
  };

  const setGeoRadiusFilter = (pins: GeoRadiusPin[] | null) => {
    store.setGeoRadiusFilter(pins);
  };

  /**
   * Card-layout column visibility (opt-in, separate from the table's
   * columnHidden): explicit entry wins, otherwise only the entity's card
   * DEFAULT columns are on. See persistentTableStore.cardColumnVisible.
   * A custom list's schedule columns are table columns only. The card draws
   * its schedules as lines of their own.
   */
  const cardVisibleColumns = computed(() => {
    const explicit = store.cardColumnVisible;
    const defaults = new Set(cardDefaultColumnIdsFor(store.storeKey));
    return absoluteOrderedColumns.value.filter(
      (column) =>
        (explicit[column.id] ?? defaults.has(column.id)) && customListScheduleFieldOf(column.key) === null,
    );
  });

  const setCardColumnVisibility = (visibility: Record<string, boolean>) => {
    store.cardColumnVisible = { ...store.cardColumnVisible, ...visibility };
  };

  /**
   * "Reset columns" in the Column settings panel. A card layout resets the
   * card's shown fields and leaves the table's own shown and pinned columns
   * alone. See persistentTableStore.resetColumnsToDefaults.
   */
  const resetColumnsToDefaults = () => {
    store.resetColumnsToDefaults({
      cardLayout: store.tableLayout === 'cards' || store.tableLayout === 'fixed-cards',
    });
  };

  return {
    // State
    storeKey: store.storeKey,
    /**
     * The models.json entity the columns come from. It differs from storeKey
     * on Track Matching, whose state is kept apart but whose columns are
     * LiveTrack's.
     */
    entityId: selectedEntityId,
    columns,
    columnsForFilter,
    columnSort,
    groupBy,
    setGroupBy,
    effectiveSort,
    absoluteOrderedColumns,
    visibleNonPinnedColumns,
    visiblePinnedColumns,
    pinnedColumns,
    unpinnedColumns,
    filter,
    compositeFilter,
    searchViewMode,
    setSearchViewMode,
    cardVisibleColumns,
    setCardColumnVisibility,
    tableLayout,
    setTableLayout,
    mapCardFieldVisibility,
    setMapCardFieldVisibility,
    setGeoRadiusFilter,
    pageSize,
    pageIndex,
    // Methods
    toggleSort,
    reorderColumns,
    // Column size methods
    setColumnSize,
    updateColumnState,
    resetColumnsToDefaults,

    // Pagination methods
    setPageSize,
    setPageIndex,
    // Filter methods
    setFilter,
  };
}
